import { Link as MuiLink, ButtonProps as MuiButtonProps, LinkProps as MuiLinkProps } from '@mui/material';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { Button, IButtonProps } from '@components/button';
import { ChevronDown } from '@assets/svgs';

import React, { MouseEvent, useState } from 'react';
import { IMenuItem, Menu } from '@components/menu/Menu';

type LinkProps = {
  type: 'link';
  linkProps: RouterLinkProps;
  buttonProps: MuiButtonProps;
};

type ExternalLinkProps = {
  type: 'externalLink';
  externalLinkProps: MuiLinkProps;
  buttonProps: MuiButtonProps;
};

type ButtonProps = {
  type: 'button';
  buttonProps: IButtonProps;
};

export type HeaderButtonProps = LinkProps | ExternalLinkProps | ButtonProps;

const HeaderButton = (props: HeaderButtonProps) => {
  const { type } = props;

  if (type === 'button') {
    const {
      buttonProps: { isLoading: loading, ...generalProps },
    } = props;
    return (
      <Button
        variant='contained'
        isLoading={!!loading}
        data-testid={generalProps.children ?? ''}
        sx={{ ...generalProps.sx }}
      />
    );
  }

  if (type === 'link') {
    const { linkProps, buttonProps } = props;
    return (
      <RouterLink {...linkProps}>
        <Button variant='outlined' sx={{ color: 'text.subdued', ...(buttonProps.sx as any) }} {...buttonProps} />
      </RouterLink>
    );
  }

  if (type === 'externalLink') {
    const { externalLinkProps, buttonProps } = props;
    return (
      <MuiLink {...externalLinkProps}>
        <Button variant='outlined' sx={buttonProps.sx as any} {...buttonProps} />
      </MuiLink>
    );
  }
  return null;
};

interface IHeaderButtons {
  buttons?: (HeaderButtonProps | React.ReactNode)[] | undefined;
  actionItems?: IMenuItem[];
  disableActionItems?: boolean;
}

export const HeaderButtons = ({ buttons, actionItems, disableActionItems }: IHeaderButtons) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOnClickMenuItem = (index: number) => {
    if (actionItems) {
      actionItems[index]?.action?.();
      setAnchorEl(null);
    }
  };

  return (
    <>
      {buttons &&
        buttons.map((props, index) => {
          if (React.isValidElement(props)) {
            const key = `${(props?.props as any)?.children?.props?.children}-${index}`;
            return React.cloneElement(props, { key });
          }
          return (
            <HeaderButton
              key={(props as HeaderButtonProps).buttonProps.children?.toString()}
              {...(props as HeaderButtonProps)}
            />
          );
        })}
      {actionItems && (
        <>
          <Button
            data-testid='Action Menu'
            onClick={handleMenuClick}
            variant='contained'
            endIcon={<ChevronDown width='12px' />}
            disabled={disableActionItems}
            sx={{
              '.MuiButton-endIcon': {
                marginTop: '2px',
              },
            }}
          >
            Actions
          </Button>
          <Menu
            open={Boolean(anchorEl)}
            items={actionItems}
            anchorEl={anchorEl}
            onClose={handleMenuClose}
            onClickMenuItem={handleOnClickMenuItem}
          />
        </>
      )}
    </>
  );
};
